import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { format, subDays } from 'date-fns'
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import PercentIcon from '@mui/icons-material/Percent'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import KPICard from '../components/KPICard'
import InterventionFilters from '../components/InterventionFilters'
import TrendChart from '../components/TrendChart'
import CallOutcomeChart from '../components/CallOutcomeChart'
import MerchantBreakdownTable from '../components/MerchantBreakdownTable'
import { MOCK_CALL_ANALYTICS } from '../mockData'
import { useGetCallAnalyticsQuery } from '../../../redux/interventionApiSlice'

const USE_MOCK = false

export default function CallAnalytics() {
  const [filters, setFilters] = useState({
    type: 'NDR',
    merchantOids: [],
    dateRange: { from: format(subDays(new Date(), 2), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  })

  const noMerchant = !USE_MOCK && !filters.merchantOids?.length
  const { data: apiData, isFetching } = useGetCallAnalyticsQuery(filters, { skip: USE_MOCK || noMerchant })
  const data = USE_MOCK ? MOCK_CALL_ANALYTICS : apiData
  const isLoading = USE_MOCK ? false : isFetching

  const merchantCols = [
    { key: 'merchantName', label: 'Merchant', format: 'text' },
    { key: 'totalCalls', label: 'Total Calls', format: 'number', align: 'right' },
    { key: 'connected', label: 'Connected', format: 'number', align: 'right' },
    { key: 'connectRate', label: 'Connect %', format: 'percent', align: 'right' },
    { key: 'uniqueAwbs', label: 'Unique AWBs', format: 'number', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant='h5'>Call Analytics</Typography>
        <Chip label='FutworkAI Not Activated' size='small' sx={{ bgcolor: '#FFF4E5', color: '#ED6C02', fontWeight: 700 }} />
      </Box>

      <InterventionFilters filters={filters} onChange={setFilters} />

      {noMerchant ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant='body1' sx={{ color: 'text.disabled' }}>Select a merchant to view data</Typography>
        </Box>
      ) : <>

      <Box className='kpi-row'>
        <KPICard title='Total Calls' value={data?.totalCalls} icon={<PhoneInTalkOutlinedIcon />} color='#FF6B35' loading={isLoading} />
        <KPICard title='Connected' value={data?.connected} icon={<PhoneCallbackIcon />} color='#0F4C81' loading={isLoading} />
        <KPICard title='Connect Rate' value={data?.connectRate} icon={<PercentIcon />} color='#2E7D32' format='percent' loading={isLoading} />
        <KPICard title='Unique AWBs' value={data?.uniqueAwbs} icon={<Inventory2OutlinedIcon />} color='#7B1FA2' loading={isLoading} />
        <KPICard title='Avg Duration' value={data?.avgDuration_seconds} icon={<TimerOutlinedIcon />} color='#ED6C02' format='duration' loading={isLoading} />
      </Box>

      <TrendChart
        data={data?.dailyTrend || []}
        title='Daily Call Volume'
        series={[
          { key: 'calls', label: 'Total Calls', color: '#FF6B35' },
          { key: 'connected', label: 'Connected', color: '#0F4C81' },
          { key: 'uniqueAwbs', label: 'Unique AWBs', color: '#7B1FA2' },
        ]}
        loading={isLoading}
      />

      {/* Duration is in seconds — its own chart so it isn't flattened by the call-count axis above */}
      <Box sx={{ mt: 2 }}>
        <TrendChart
          data={data?.dailyTrend || []}
          title='Avg Call Duration (seconds)'
          height={240}
          series={[{ key: 'avgDuration', label: 'Avg Duration (s)', color: '#ED6C02' }]}
          loading={isLoading}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <CallOutcomeChart data={data?.outcomeDistribution || {}} loading={isLoading} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <MerchantBreakdownTable
          data={data?.merchantBreakdown || []}
          columns={merchantCols}
          title='Calls by Merchant'
          loading={isLoading}
          defaultSort={{ field: 'totalCalls', direction: 'desc' }}
        />
      </Box>
      </>}
    </Box>
  )
}
